import { Link } from "react-router-dom";
import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { toast } from 'react-toastify';

// primereact
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

// components
import Panel from "../../../components/panel/panel";
import Select from "../../../components/input/Select";
import CalendarInput from "../../../components/input/CalendarInput";
import TableSalesSummary from "./TableSalesSummary";
import ModelReport from '../../../components/report/ModelReport';
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

// hooks
import { useOptionsSelect } from "../../../hooks/useOptionsSelect";
import { useAppContext } from "../../../context/AppContext";
import usePermission from "../../../hooks/usePermission";

// services
import { postReportSalesSummary } from "../../../services/Report/postReportSalesSumarry";
import { getBlocks } from "../../../services/GlobalRoles/getBlocks";
import { getLands } from "../../../services/GlobalRoles/getLands";
import { RadioButton } from "primereact/radiobutton";

export const SallesSummary = () => {
  const { control ,formState,handleSubmit, setValue } = useForm();
  const { errors } = formState;
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedLand, setSelectedLand] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [salesDate, setSalesDate] = useState(null);
  const [blockOptions, setBlockOptions] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [landOptions, setLandOptions] = useState([]);
  const [loadingLands, setLoadingLands] = useState(false);
  const [toastId, setToastId] = useState(null);
  const [user, setUser] = useState("");
  const [reportFilters, setReportFilters] = useState([]);
  const [filterType, setFilterType] = useState(''); 


  const {
    enterprise,
    client,
    isLoadingEnterprises,
    isLoadingClients,
  } = useOptionsSelect();
  const [loading, setLoading] = useState(false);

  const { nomenclatures } = useAppContext();
  usePermission('271')
  const nomenclature = (() => {
    try {
      return JSON.parse(nomenclatures);
    } catch {
      return [
        { value: 'Quadra' },
        { value: 'Lote' }
      ];
    }
  })();
  const headerMap = {
    block_unit: `${nomenclature[0]?.value && 'Quadra'} / ${nomenclature[1]?.value && 'Lote'}`,
    sale_data: 'Data da Venda',
    client: 'Cliente',
    sale_value: 'Valor da Venda',
    financial_value: 'Valor Financiado',
    received: 'Recebido',
    overdue: 'Em Atraso',
    to_receive: 'A Receber',
  };

  const formatDate = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    const d = new Date(date);
    return d.toISOString().slice(0, 10);
  };

  const getMonthNumber = (month) => {
    if (!month) return null;
    if (typeof month === 'number') return month;
    if (month.getMonth) return month.getMonth() + 1;
    return Number(month) || null;
  };

  const getYearNumber = (year) => {
    if (!year) return null;
    if (typeof year === 'number') return year;
    if (year.getFullYear) return year.getFullYear();
    return Number(year) || null;
  };

  const { data: reportData, refetch } = useQuery(
    "reportSalesSummary",
    () => postReportSalesSummary({
      enterprise_id: selectedEnterprise,
      block_id: selectedBlock,
      land_id: selectedLand,
      client_id: selectedClient,
      year: getYearNumber(selectedYear),
      month: getMonthNumber(selectedMonth),
      period_start: formatDate(periodStart),
      period_end: formatDate(periodEnd),
      sales_date: formatDate(salesDate),
    }),
    {
      enabled: false,
      onSuccess: (data) => {
        toast.update(toastId, {
          render: 'Relatório gerado com sucesso!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        setLoading(false);
      },
      onError: (error) => {
        toast.update(toastId, {
          render: 'Erro ao gerar relatório!',
          type: 'error',
          isLoading: false,
          autoClose: 4000,
        });
        setLoading(false);
      },
    }
  ); 

  useEffect(() => {
    if (selectedEnterprise) {
      setLoadingBlocks(true);
      getBlocks(selectedEnterprise)
        .then((data) => {
          const arr = Object.values(data).map((item) => ({
            value: item.id,
            label: item.name,
          }));
          setBlockOptions(arr);
        })
        .finally(() => setLoadingBlocks(false));
    } else {
      setBlockOptions([]);
      setSelectedBlock(null);
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    if (selectedBlock) {
      setLoadingLands(true);
      getLands(selectedBlock)
        .then((lands) => {
          const arr = (lands || []).map((item) => ({
            value: item.id,
            label: item.name,
          }));
          setLandOptions(arr);
        })
        .finally(() => setLoadingLands(false));
    } else {
      setLandOptions([]);
      setSelectedLand(null);
    }
  }, [selectedBlock]);

  useEffect(() => {
    if (filterType === 'year-month') {
      setPeriodStart(null);
      setPeriodEnd(null);
      setSalesDate(null);
      setValue('period_start', null);
      setValue('period_end', null);
      setValue('sales_date', null);
    } else if (filterType === 'period') {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSalesDate(null);
      setValue('year', null);
      setValue('month', null);
      setValue('sales_date', null);
    } else if (filterType === 'sales-date') {
      setSelectedYear(null);
      setSelectedMonth(null);
      setPeriodStart(null);
      setPeriodEnd(null);
      setValue('year', null);
      setValue('month', null);
      setValue('period_start', null);
      setValue('period_end', null);
    }
  }, [filterType]);

  const items = [
    {
      template: () => (
        <Link to={"/reports/sales-summary"} className="p-breadcrumb-link">
          Resumo de vendas
        </Link>
      ),
    },
  ];

  const home = {
    template: () => (
      <Link to={"#"} className="p-breadcrumb-link">
        <i className="fa fa-home"></i>
      </Link>
    ),
  };

  const onSubmit = async (e) => {
    if (!selectedEnterprise) {
      toast.warn('O campo Empreendimento é obrigatório!');
      return;
    }
    setLoading(true);
    const id = toast.loading('Buscando relatório, aguarde...');
    setToastId(id);
    setReportFilters(getFilters()); 
    refetch();
  }    

  const getFilters = () => {
    const formatDateBR = (date) => {
      if (!date) return 'N/A';
      if (typeof date === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          const [y, m, d] = date.split('-');
          return `${d}/${m}/${y}`;
        }
        return date;
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString('pt-BR');
      }
      return String(date);
    };
    const getMonthNumber = (month) => {
      if (!month) return 'N/A';
      if (typeof month === 'number') return month;
      if (month.getMonth) return month.getMonth() + 1;
      return Number(month) || 'N/A';
    };
    const getYearNumber = (year) => {
      if (!year) return 'N/A';
      if (typeof year === 'number') return year;
      if (year.getFullYear) return year.getFullYear();
      return Number(year) || 'N/A';
    };
    return [
      {
        title: 'Empreendimento',
        content: selectedEnterprise ? enterprise?.find(e => e.value === selectedEnterprise)?.label : 'N/A',
      },
      {
        title: nomenclature[0]?.value || 'Quadra',
        content: selectedBlock ? blockOptions?.find(b => b.value === selectedBlock)?.label : 'N/A',
      },
      {
        title: nomenclature[1]?.value || 'Lote',
        content: selectedLand ? landOptions?.find(l => l.value === selectedLand)?.label : 'N/A',
      },
      {
        title: 'Cliente',
        content: selectedClient ? client?.find(c => c.value === selectedClient)?.label : 'N/A',
      },
      {
        title: 'Ano',
        content: getYearNumber(selectedYear),
      },
      {
        title: 'Mês',
        content: getMonthNumber(selectedMonth),
      },
      {
        title: 'Período Inicial',
        content: formatDateBR(periodStart),
      },
      {
        title: 'Período Final',
        content: formatDateBR(periodEnd),
      },
      {
        title: 'Data da Venda',
        content: formatDateBR(salesDate),
      },
    ];
  };

  const handleExportPDF = () => {
    const element = document.getElementById("sales-summary-report");
    if (!element) return;

    const options = {
      margin: 10,
      filename: "resumo-vendas.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2,  },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
    };

    html2pdf()
      .from(element)
      .set(options)
      .save()
      .then(() => {
        element.classList.remove("force-light");
      })
      .catch(() => {
        element.classList.remove("force-light");
      });
  };

  const handleExportExcel = () => {
    if (!reportData) return;
    let rows = reportData.rows || reportData.data || [];
    if (!Array.isArray(rows) || rows.length === 0) {
      rows = [reportData];
    }
    const translatedRows = rows.map(row => {
      const newRow = {};
      Object.keys(row).forEach(key => {
        if (key === 'client') {
          newRow[headerMap[key] || key] = row.client?.name || '';
        } else {
          newRow[headerMap[key] || key] = row[key];
        }
      });
      return newRow;
    });
    const ws = XLSX.utils.json_to_sheet(translatedRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ResumoVendas");
    XLSX.writeFile(wb, "resumo-vendas.xlsx");
  };

  return (
    <>
    <BreadCrumb
    model={items}
    home={home}
    style={{
          background: "transparent",
          border: "none",
          fontSize: "16px",
    }}
      />

      <Panel title=" Resumo de Vendas" className="p-4 mb-4">
        <form onSubmit={handleSubmit(onSubmit)} className="mb-4">
        <div className="row align-items-end py-2">
          <div className="col-xl-2 col-md-4">
            <Select
              name="enterprise_id"
              label={isLoadingEnterprises ? "Carregando..." : "Empreendimento *"}
              options={enterprise}
              optionLabel="label"
              optionValue="value"
              value={selectedEnterprise}
              onChange={setSelectedEnterprise}
              isRequired
              disabled={isLoadingEnterprises}
              control={control}
              className="w-100"
              errors={errors}
            />
          </div>
          <div className="col-xl-2 col-md-4">
            <Select
              name="block_id"
              label={loadingBlocks ? "Carregando..." : nomenclature[0]?.value || "Quadra"}
              options={blockOptions}
              optionLabel="label"
              optionValue="value"
              value={selectedBlock}
              onChange={setSelectedBlock}
              disabled={loadingBlocks || !selectedEnterprise}
              control={control}
              className="w-100"
            />
          </div>
          <div className="col-xl-2 col-md-4">
            <Select
              name="land_id"
              label={loadingLands ? "Carregando..." : nomenclature[1]?.value || "Lote"}
              options={landOptions}
              optionLabel="label"
              optionValue="value"
              value={selectedLand}
              onChange={setSelectedLand}
              disabled={loadingLands || !selectedBlock}
              control={control}
              className="w-100"
            />
          </div>
          <div className="col-xl-2 col-md-4">
            <Select
              name="client_id"
              label={isLoadingClients ? "Carregando..." : "Cliente"}
              options={client}
              optionLabel="label"
              optionValue="value"
              value={selectedClient}
              onChange={setSelectedClient}
              disabled={isLoadingClients || !selectedEnterprise}
              control={control}
              placeholder={isLoadingClients ? "Carregando..." : "Selecione"}
              className="w-100"
            />
          </div>
          <div className="col-xl-4 col-md-12 d-flex align-items-center gap-2 mt-2">
            <RadioButton
              inputId="year-month"
              name="filterType"
              value="year-month"
              checked={filterType === 'year-month'}
              onChange={() => setFilterType('year-month')}
              className="mx-2"
            />
            <label htmlFor="year-month" className="mx-2">Mês/Ano</label>
            <RadioButton
              inputId="period"
              name="filterType"
              value="period"
              checked={filterType === 'period'}
              onChange={() => setFilterType('period')}
              className="mx-2"
            />
            <label htmlFor="period" className="mx-2">Período</label>
            <RadioButton
              inputId="sales-date"
              name="filterType"
              value="sales-date"
              checked={filterType === 'sales-date'}
              onChange={() => setFilterType('sales-date')}
              className="mx-2"
            />
            <label htmlFor="sales-date" className="mx-2">Data da Venda</label>
          </div>
        </div>
        <div className="row align-items-end py-2">
          {filterType === 'year-month' && (
            <>
              <div className="col-xl-3 col-md-4">
                <CalendarInput
                  name="month"
                  label="Mês"
                  control={control}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.value)}
                  view="month"
                  dateFormat="mm"
                  mask={null}
                  placeholder="Mês"
                  className="w-100"
                />
              </div>
              <div className="col-xl-3 col-md-4">
                <CalendarInput
                  name="year"
                  label="Ano"
                  control={control}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.value)}
                  view="year"
                  dateFormat="yy"
                  mask={null}
                  placeholder="Ano"
                  className="w-100"
                />
              </div>
            </>
          )}
          {filterType === 'period' && (
            <>
              <div className="col-md-3">
                <CalendarInput
                  name="period_start"
                  label="Período Inicial"
                  control={control}
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.value)}
                  className="w-100"
                />
              </div>
              <div className="col-md-3">
                <CalendarInput
                  name="period_end"
                  label="Período Final"
                  control={control}
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.value)}
                  className="w-100"
                />
              </div>
            </>
          )}
          {filterType === 'sales-date' && (
            <div className="col-md-3">
              <CalendarInput
                name="sales_date"
                label="Data da Venda"
                control={control}
                value={salesDate}
                onChange={(e) => setSalesDate(e.value)}
                className="w-100"
              />
            </div>
          )}
          <div className="w-100 d-flex justify-content-end">
            <Button
              className="p-button-primary w-10 mt-3"
              icon={loading ? 'pi pi-spinner pi-spin' : 'pi pi-search'}
              type="submit"
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
        </form>
      </Panel>


      <div className="mt-5">
        {loading && (
          <Panel>
            <div className="p-4">
              <div className="mb-3">
                <Skeleton height="2rem" width="30%" className="mb-2" />
                <Skeleton height="2rem" width="100%" className="mb-2" />
                <Skeleton height="2rem" width="100%" className="mb-2" />
                <Skeleton height="2rem" width="100%" className="mb-2" />
                <Skeleton height="2rem" width="100%" className="mb-2" />
              </div>
              <Skeleton height="3rem" width="100%" className="mb-2" />
              <Skeleton height="3rem" width="100%" className="mb-2" />
              <Skeleton height="3rem" width="100%" className="mb-2" />
            </div>
          </Panel>
        )}
        {!loading && reportData &&
          <Panel>
            <div className="d-flex gap-2 mb-3 justify-content-end">
              <Button icon="fa fa-file-pdf"  className="p-button-secondary cursor-pointer" rounded onClick={handleExportPDF} />
              <Button icon="fa fa-file-excel"  className="p-button-success cursor-pointer" rounded onClick={handleExportExcel} />
            </div>
            <div id="sales-summary-report">
              <ModelReport
                title={reportData.enterprise_title || "Resumo de Vendas"}
                logo={reportData.enterprise?.img ? `${reportData.enterprise.img}` : (reportData.enterprise?.logo || "sem logo")}
                date={`Emitido em ${new Date().toLocaleDateString('pt-BR')}`}
                madeBy={reportData.user?.name || ""}
                filters={reportFilters}
              >
                <TableSalesSummary table={reportData} />
              </ModelReport>
            </div>
          </Panel>
        }
      </div>
      
    </>
  );
};


